import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Sun, Zap, TrendingUp, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const SolarReadings = ({ user }) => {
  const [readings, setReadings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    a1Current: 0,
    a1Previous: 0,
    a2Current: 0,
    a2Previous: 0,
    a3Current: 0,
    a3Previous: 0,
    notes: ''
  });

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [readingsRes, statsRes] = await Promise.all([
        axiosInstance.get('/solar-readings'),
        axiosInstance.get('/solar-readings/stats')
      ]);
      setReadings(readingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axiosInstance.put(`/solar-readings/${editingId}`, formData);
        toast.success('Lettura aggiornata!');
      } else {
        await axiosInstance.post('/solar-readings', formData);
        toast.success('Lettura registrata!');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa lettura?')) return;
    try {
      await axiosInstance.delete(`/solar-readings/${id}`);
      toast.success('Lettura eliminata!');
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const openEditDialog = (reading) => {
    setFormData({
      month: reading.month,
      year: reading.year,
      a1Current: reading.a1Current,
      a1Previous: reading.a1Previous,
      a2Current: reading.a2Current,
      a2Previous: reading.a2Previous,
      a3Current: reading.a3Current,
      a3Previous: reading.a3Previous,
      notes: reading.notes || ''
    });
    setEditingId(reading.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      a1Current: 0,
      a1Previous: 0,
      a2Current: 0,
      a2Previous: 0,
      a3Current: 0,
      a3Previous: 0,
      notes: ''
    });
    setEditingId(null);
  };

  const calculateProductions = () => {
    const a1Production = formData.a1Current - formData.a1Previous;
    const a2Production = formData.a2Current - formData.a2Previous;
    const a3Production = formData.a3Current - formData.a3Previous;
    const totalProduction = a1Production + a2Production + a3Production;
    return { a1Production, a2Production, a3Production, totalProduction };
  };

  const canEdit = user?.role === 'admin' || user?.role === 'maintainer';
  const canDelete = user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalProduction = formData.a1 + formData.a2 + formData.a3;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="solar-readings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Fotovoltaico</h1>
          <p className="text-gray-600">Letture emissioni impianti fotovoltaici A1, A2, A3</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetForm}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                data-testid="add-solar-reading-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Lettura
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="solar-reading-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Modifica Lettura' : 'Nuova Lettura Fotovoltaico'}</DialogTitle>
                <DialogDescription>
                  Inserisci le letture degli inverter fotovoltaici
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="month">Mese</Label>
                    <select
                      id="month"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      data-testid="solar-month-select"
                    >
                      {monthNames.map((name, index) => (
                        <option key={index + 1} value={index + 1}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="year">Anno</Label>
                    <Input
                      id="year"
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      required
                      data-testid="solar-year-input"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Impianto A1</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="a1Current">Lettura Corrente (kWh)</Label>
                      <Input
                        id="a1Current"
                        type="number"
                        step="0.01"
                        value={formData.a1Current}
                        onChange={(e) => setFormData({ ...formData, a1Current: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a1-current-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="a1Previous">Lettura Precedente (kWh)</Label>
                      <Input
                        id="a1Previous"
                        type="number"
                        step="0.01"
                        value={formData.a1Previous}
                        onChange={(e) => setFormData({ ...formData, a1Previous: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a1-previous-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-cyan-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Impianto A2</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="a2Current">Lettura Corrente (kWh)</Label>
                      <Input
                        id="a2Current"
                        type="number"
                        step="0.01"
                        value={formData.a2Current}
                        onChange={(e) => setFormData({ ...formData, a2Current: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a2-current-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="a2Previous">Lettura Precedente (kWh)</Label>
                      <Input
                        id="a2Previous"
                        type="number"
                        step="0.01"
                        value={formData.a2Previous}
                        onChange={(e) => setFormData({ ...formData, a2Previous: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a2-previous-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-medium text-gray-900">Impianto A3</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="a3Current">Lettura Corrente (kWh)</Label>
                      <Input
                        id="a3Current"
                        type="number"
                        step="0.01"
                        value={formData.a3Current}
                        onChange={(e) => setFormData({ ...formData, a3Current: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a3-current-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="a3Previous">Lettura Precedente (kWh)</Label>
                      <Input
                        id="a3Previous"
                        type="number"
                        step="0.01"
                        value={formData.a3Previous}
                        onChange={(e) => setFormData({ ...formData, a3Previous: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="solar-a3-previous-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg space-y-2">
                  <h4 className="font-medium text-gray-900 mb-3">Produzioni Calcolate</h4>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Produzione A1</p>
                      <p className="text-xl font-bold text-blue-600">
                        {calculateProductions().a1Production.toFixed(2)} kWh
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Produzione A2</p>
                      <p className="text-xl font-bold text-cyan-600">
                        {calculateProductions().a2Production.toFixed(2)} kWh
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Produzione A3</p>
                      <p className="text-xl font-bold text-purple-600">
                        {calculateProductions().a3Production.toFixed(2)} kWh
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Totale</p>
                      <p className="text-xl font-bold text-amber-600">
                        {calculateProductions().totalProduction.toFixed(2)} kWh
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Note</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                    data-testid="solar-notes-textarea"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  data-testid="solar-submit-button"
                >
                  {editingId ? 'Aggiorna Lettura' : 'Registra Lettura'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      {stats && stats.totalReadings > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Sun className="w-4 h-4 mr-2" />
                Produzione Totale
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-amber-600">{stats.totalProduction.toFixed(2)} kWh</p>
              <p className="text-xs text-gray-500 mt-1">Storica</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Media Mensile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-orange-600">{stats.averageProduction.toFixed(2)} kWh</p>
              <p className="text-xs text-gray-500 mt-1">Per mese</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <Zap className="w-4 h-4 mr-2" />
                Letture Totali
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">{stats.totalReadings}</p>
              <p className="text-xs text-gray-500 mt-1">Registrate</p>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600">Anni Monitorati</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-600">{Object.keys(stats.yearlyStats).length}</p>
              <p className="text-xs text-gray-500 mt-1">Anni</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Readings by Year */}
      {stats && stats.yearlyStats && Object.keys(stats.yearlyStats).length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle>Riepilogo Annuale</CardTitle>
            <CardDescription>Produzione per anno</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(stats.yearlyStats).sort((a, b) => b[0] - a[0]).map(([year, data]) => (
                <div key={year} className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{year}</h3>
                    <span className="badge badge-yellow">{data.months} mesi</span>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Totale</p>
                      <p className="text-lg font-bold text-amber-600">{data.production.toFixed(2)} kWh</p>
                    </div>
                    <div>
                      <p className="text-gray-600">A1</p>
                      <p className="text-lg font-bold text-blue-600">{data.a1.toFixed(2)} kWh</p>
                    </div>
                    <div>
                      <p className="text-gray-600">A2</p>
                      <p className="text-lg font-bold text-cyan-600">{data.a2.toFixed(2)} kWh</p>
                    </div>
                    <div>
                      <p className="text-gray-600">A3</p>
                      <p className="text-lg font-bold text-purple-600">{data.a3.toFixed(2)} kWh</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Readings Table */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle>Letture Mensili</CardTitle>
          <CardDescription>Dettaglio emissioni per mese</CardDescription>
        </CardHeader>
        <CardContent>
          {readings.length === 0 ? (
            <p className="text-gray-500 text-center py-8" data-testid="no-readings">Nessuna lettura disponibile</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="readings-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Periodo</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">A1 (kWh)</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">A2 (kWh)</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">A3 (kWh)</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Totale (kWh)</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Registrato da</th>
                    {canEdit && <th className="text-center py-3 px-4 font-medium text-gray-700">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {readings.map((reading) => (
                    <tr key={reading.id} className="border-b border-gray-100 hover:bg-amber-50 transition-colors" data-testid={`reading-row-${reading.id}`}>
                      <td className="py-3 px-4 font-medium">{monthNames[reading.month - 1]} {reading.year}</td>
                      <td className="py-3 px-4 text-right text-blue-600">{reading.a1Production.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right text-cyan-600">{reading.a2Production.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right text-purple-600">{reading.a3Production.toFixed(2)}</td>
                      <td className="py-3 px-4 text-right font-bold text-amber-600">{reading.totalProduction.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{reading.recordedBy}</td>
                      {canEdit && (
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openEditDialog(reading)}
                              className="p-1.5 rounded-lg hover:bg-blue-100"
                              data-testid={`edit-reading-${reading.id}`}
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(reading.id)}
                                className="p-1.5 rounded-lg hover:bg-red-100"
                                data-testid={`delete-reading-${reading.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SolarReadings;